import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowRight, HardHat } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { fetchDashboard, fetchPendingTimeApprovals, approveTimeCorrection, rejectTimeCorrection } from '../lib/bridge';
import { LoadingState } from '../components/ui/LoadingState';
import { ErrorState } from '../components/ui/ErrorState';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { MetricCard } from '../components/ui/MetricCard';
import { PageHeader } from '../components/ui/PageHeader';

// Stage 2 (docs/V6_ARCHITECTURE_AND_MIGRATION_PLAN.md §8): the daily-use
// office dashboard, reusing the exact same scoped metrics/role-dashboard
// data the current HTML UI already computes (appBuildBootstrapForUser_),
// plus pending worker time-correction approvals — unmodified
// appApproveWorkerTimeAdjustment/appRejectWorkerTimeAdjustment, gated on
// canManageUsers (permissions.users === true), the same server-computed
// permission appGetPendingAdminApprovals itself checks. This is a
// per-account checkbox an Owner can grant to any role, not just
// Owner/Admin, so it must not be hardcoded to a role list here.
export function DashboardPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['dashboard'], queryFn: fetchDashboard });
  const canApproveTime = !!user?.canManageUsers;
  const timeApprovalsQuery = useQuery({ queryKey: ['pendingTimeApprovals'], queryFn: fetchPendingTimeApprovals, enabled: canApproveTime });

  const approveMutation = useMutation({
    mutationFn: (entryId: string) => approveTimeCorrection(entryId),
    onSuccess: (result) => result.ok && void queryClient.invalidateQueries({ queryKey: ['pendingTimeApprovals'] }),
  });
  const rejectMutation = useMutation({
    mutationFn: (entryId: string) => rejectTimeCorrection(entryId),
    onSuccess: (result) => result.ok && void queryClient.invalidateQueries({ queryKey: ['pendingTimeApprovals'] }),
  });

  if (query.isLoading) return <LoadingState label="Loading dashboard…" />;
  if (query.isError) {
    return <ErrorState message={query.error instanceof Error ? query.error.message : 'Could not load the dashboard.'} onRetry={() => query.refetch()} />;
  }
  if (!query.data?.ok) {
    return <ErrorState message={query.data?.message || 'Could not load the dashboard.'} onRetry={() => query.refetch()} />;
  }

  const { dashboard, roleDashboard } = query.data;
  const timeApprovals = timeApprovalsQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Overview" title={roleDashboard?.title || 'Dashboard'} subtitle={`Signed in as ${user?.name} · ${user?.role.replace(/_/g, ' ')}`} />

      {dashboard && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MetricCard label="Active Jobs" value={dashboard.metrics.activeJobs} />
          <MetricCard label="Waiting Estimates" value={dashboard.metrics.waitingEstimates} />
          <MetricCard label="Unpaid Invoices" value={dashboard.metrics.unpaidInvoices} />
          <MetricCard label="Outstanding" value={dashboard.metrics.outstandingText} money />
        </div>
      )}

      {typeof roleDashboard?.assignedJobs === 'number' && (
        <Card className="flex flex-wrap items-center justify-between gap-3" raised>
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-gold-700 bg-gold-900/40 text-gold-200">
              <HardHat className="h-5 w-5" aria-hidden="true" />
            </span>
            <p className="text-sm text-slate-300">
              You have <span className="font-semibold text-white">{roleDashboard.assignedJobs}</span> assigned job
              {roleDashboard.assignedJobs === 1 ? '' : 's'}.
            </p>
          </div>
          <Link to="/field" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold-300 hover:text-gold-200">
            View Field Operations
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </Card>
      )}

      {canApproveTime && timeApprovals.length > 0 && (
        <Card>
          <CardHeader title="Pending Time Corrections" subtitle="Worker-requested clock-out adjustments awaiting review." />
          <ul className="space-y-2">
            {timeApprovals.map((item) => (
              <li key={item.entryId} className="rounded-xl border border-ink-border bg-ink p-3.5 text-sm">
                <p className="font-semibold text-white">
                  {item.workerName} <span className="font-normal text-slate-500">— {item.customer || item.jobId}</span>
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {item.currentEndTime} → {item.requestedEndTime} · {item.correctionReason}
                </p>
                <div className="mt-2.5 flex gap-2">
                  <Button variant="primary" busy={approveMutation.isPending} busyLabel="Approving…" onClick={() => approveMutation.mutate(item.entryId)}>
                    Approve
                  </Button>
                  <Button variant="secondary" busy={rejectMutation.isPending} busyLabel="Rejecting…" onClick={() => rejectMutation.mutate(item.entryId)}>
                    Reject
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {dashboard && (
        <Card>
          <CardHeader title="Recent Jobs" />
          {dashboard.recentJobs.length === 0 ? (
            <EmptyState>No recent jobs yet.</EmptyState>
          ) : (
            <ul className="space-y-2">
              {dashboard.recentJobs.map((job) => (
                <li key={job.jobId}>
                  <Link
                    to={`/jobs/${encodeURIComponent(job.jobId)}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-ink-border bg-ink p-3.5 transition hover:border-gold-700/60 hover:bg-ink-raised"
                  >
                    <span className="truncate text-sm font-semibold text-white">{job.customer || job.jobId}</span>
                    <span className="shrink-0 text-xs font-medium text-slate-400">{job.stage || job.estimateStatus || '—'}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
